import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../services/api';
import { toast } from 'react-hot-toast';
import { Search, Plus, Package, Gem, Tag, Edit2, X, ChevronLeft, ChevronRight, ZoomIn } from 'lucide-react';

const SOURCE_LABELS = { C: 'RMB', M: 'MYR', U: 'USD' };
const PREFIX_CATS = ['VIP', 'GRA', 'SET'];
const fmt6 = n => String(n || 0).padStart(6, '0');

// ─── Photo thumbnail ─────────────────────────────────────────────────────────
function PhotoCell({ url }) {
  const [open, setOpen] = useState(false);
  if (!url) return (
    <div style={{ width: 44, height: 44, background: '#f3f4f6', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Package size={18} color="#9ca3af" />
    </div>
  );
  return (
    <>
      <div onClick={() => setOpen(true)} style={{ width: 44, height: 44, borderRadius: 6, overflow: 'hidden', cursor: 'zoom-in', border: '1px solid #e5e7eb', position: 'relative' }}>
        <img src={url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
      </div>
      {open && (
        <div onClick={() => setOpen(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <img src={url} alt="" style={{ maxWidth: '90vw', maxHeight: '90vh', borderRadius: 8 }} />
        </div>
      )}
    </>
  );
}

// ─── Stone searchable dropdown ────────────────────────────────────────────────
function StoneSearch({ onSelect, placeholder }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const timer = useRef(null);
  const wrapRef = useRef(null);

  useEffect(() => {
    const handler = e => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const search = useCallback(async (term) => {
    setLoading(true);
    try {
      const res = await api.get('/stock/stones', { params: { q: term || ' ' } });
      setResults(res.data);
    } catch { } finally { setLoading(false); }
  }, []);

  const handleInput = e => {
    setQ(e.target.value);
    setOpen(true);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => search(e.target.value), 250);
  };

  const select = stone => {
    onSelect(stone);
    setQ('');
    setOpen(false);
    setResults([]);
  };

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <div style={{ position: 'relative' }}>
        <Search size={13} style={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
        <input value={q} onChange={handleInput} onFocus={() => { setOpen(true); if (!results.length) search(''); }}
          placeholder={placeholder || "Type code or name (EN/KH/CN)..."}
          style={{ width: '100%', padding: '8px 12px 8px 28px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' }} />
      </div>
      {open && (
        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 200, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 6, boxShadow: '0 8px 24px rgba(0,0,0,0.12)', maxHeight: 240, overflowY: 'auto', marginTop: 2 }}>
          {loading && <div style={{ padding: '10px 14px', color: '#6b7280', fontSize: 13 }}>Searching...</div>}
          {!loading && results.length === 0 && <div style={{ padding: '10px 14px', color: '#6b7280', fontSize: 13 }}>No results</div>}
          {results.map(s => (
            <div key={s.id} onMouseDown={() => select(s)}
              style={{ padding: '8px 14px', cursor: 'pointer', borderBottom: '1px solid #f3f4f6', display: 'flex', gap: 10, alignItems: 'center' }}
              onMouseEnter={e => e.currentTarget.style.background = '#f0f9ff'}
              onMouseLeave={e => e.currentTarget.style.background = ''}>
              <span style={{ fontFamily: 'monospace', fontSize: 12, background: '#e0f2fe', color: '#0369a1', padding: '2px 6px', borderRadius: 4, minWidth: 36, textAlign: 'center' }}>{s.code}{s.sub}</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, color: '#111827' }}>{s.name_en}</div>
                <div style={{ fontSize: 11, color: '#6b7280' }}>{s.name_kh}{s.name_cn ? ` / ${s.name_cn}` : ''}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Modal wrapper ────────────────────────────────────────────────────────────
function Modal({ title, onClose, children, wide }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ background: '#fff', borderRadius: 10, width: '100%', maxWidth: wide ? 720 : 500, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid #e5e7eb', position: 'sticky', top: 0, background: '#fff', zIndex: 1 }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: '#111827' }}>{title}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280' }}><X size={18} /></button>
        </div>
        <div style={{ padding: 20 }}>{children}</div>
      </div>
    </div>
  );
}

const inputStyle = { width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' };
const btnPrimary = { background: '#2563eb', color: '#fff', border: 'none', borderRadius: 6, padding: '9px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer' };
const btnGhost = { background: 'none', border: '1px solid #d1d5db', borderRadius: 6, padding: '7px 14px', fontSize: 12, cursor: 'pointer', color: '#374151' };

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>{label}</label>
      {children}
    </div>
  );
}

// ─── Add Stock Modal ──────────────────────────────────────────────────────────
function AddStockModal({ categories, onClose, onSaved }) {
  const [form, setForm] = useState({
    prefix: '', category_id: '', category_code: '',
    sub_categories: '',
    name_kh: '', name_en: '', name_cn: '',
    cost_price: '', selling_price: '', source: 'C',
    quantity: 0, low_stock_alert: 5, photo_url: '', is_active: 1,
  });
  const [stones, setStones] = useState([]); // array of {code, sub, name_en, name_kh, name_cn}
  const [nextSerial, setNextSerial] = useState(null);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // Separate normal vs prefix categories
  const prefixCats = categories.filter(c => PREFIX_CATS.includes(c.code));
  const normalCats = categories.filter(c => !PREFIX_CATS.includes(c.code));

  useEffect(() => {
    if (form.category_code) {
      api.get(`/stock/items/next-serial/${form.prefix || form.category_code}`)
        .then(r => setNextSerial(r.data.formatted)).catch(() => {});
    }
  }, [form.category_code, form.prefix]);

  const handleCatChange = e => {
    const cat = categories.find(c => c.id === parseInt(e.target.value));
    if (!cat) return;
    if (PREFIX_CATS.includes(cat.code)) {
      set('prefix', cat.code); set('category_id', ''); set('category_code', '');
    } else {
      set('prefix', ''); set('category_id', cat.id); set('category_code', cat.code);
    }
  };

  const handleSubCatChange = e => {
    const cat = categories.find(c => c.id === parseInt(e.target.value));
    if (cat) { set('category_id', cat.id); set('category_code', cat.code); }
  };

  const addStone = stone => {
    if (stones.find(s => s.code === stone.code && s.sub === stone.sub)) return;
    setStones(prev => [...prev, stone]);
  };

  const removeStone = idx => setStones(prev => prev.filter((_, i) => i !== idx));

  const stoneCodes = stones.map(s => `${s.code}${s.sub}`).join('/');

  // Build preview code
  const buildPreview = () => {
    const serial = nextSerial || '??????';
    const cat = form.category_code;
    if (!cat && !form.prefix) return '—';
    if (form.prefix === 'VIP' || form.prefix === 'GRA') return `${form.prefix} ${cat} ${stoneCodes || '??'} ${serial}`;
    if (form.prefix === 'SET') return `SET ${form.sub_categories || '??'} ${stoneCodes || '??'} ${serial}`;
    return `${cat} ${stoneCodes || '??'} ${serial}`;
  };

  const submit = async () => {
    if (!form.category_id) return toast.error('Select category');
    if (stones.length === 0) return toast.error('Add at least one stone type');
    setSaving(true);
    try {
      await api.post('/stock/items', {
        ...form,
        stone_code: stones[0] ? `${stones[0].code}${stones[0].sub}` : '',
        stone_codes: stoneCodes,
      });
      toast.success('Item added');
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setSaving(false); }
  };

  const isPrefix = !!form.prefix;
  const isSet = form.prefix === 'SET';

  return (
    <Modal title="Add Stock Item" onClose={onClose} wide>
      {/* Code preview */}
      <div style={{ background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 6, padding: '8px 14px', fontSize: 14, fontFamily: 'monospace', color: '#0369a1', fontWeight: 700, marginBottom: 16 }}>
        Code preview: {buildPreview()}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        {/* Category selector */}
        <Field label="Category *">
          <select onChange={handleCatChange} style={inputStyle} defaultValue="">
            <option value="">Select...</option>
            <optgroup label="── Normal ──">
              {normalCats.map(c => <option key={c.id} value={c.id}>{c.code} — {c.name_en}{c.name_kh ? ` / ${c.name_kh}` : ''}{c.name_cn ? ` / ${c.name_cn}` : ''}</option>)}
            </optgroup>
            <optgroup label="── Special ──">
              {prefixCats.map(c => <option key={c.id} value={c.id}>{c.code}</option>)}
            </optgroup>
          </select>
        </Field>

        {/* Sub-category for VIP/GRA */}
        {(form.prefix === 'VIP' || form.prefix === 'GRA') && (
          <Field label={`${form.prefix} Sub-Category *`}>
            <select onChange={handleSubCatChange} style={inputStyle} defaultValue="">
              <option value="">Select type...</option>
              {normalCats.map(c => <option key={c.id} value={c.id}>{c.code} — {c.name_en}</option>)}
            </select>
          </Field>
        )}

        {/* Sub-categories for SET (free text e.g. NK ER RG) */}
        {isSet && (
          <Field label="SET includes (e.g. NK ER RG) *">
            <input value={form.sub_categories} onChange={e => set('sub_categories', e.target.value)} style={inputStyle} placeholder="NK ER RG" />
          </Field>
        )}

        {/* Stone types */}
        <div style={{ gridColumn: '1/-1' }}>
          <Field label="Stone Type(s) *">
            <StoneSearch onSelect={addStone} placeholder="Type code (01A) or name to add stone..." />
            {stones.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
                {stones.map((s, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 4, background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, padding: '5px 10px', fontSize: 12 }}>
                    <span style={{ fontFamily: 'monospace', fontWeight: 700, color: '#1d4ed8' }}>{s.code}{s.sub}</span>
                    <div>
                      <div style={{ color: '#374151', fontWeight: 500 }}>{s.name_en}</div>
                      {(s.name_kh || s.name_cn) && <div style={{ fontSize: 11, color: '#6b7280' }}>{s.name_kh}{s.name_kh && s.name_cn ? ' / ' : ''}{s.name_cn}</div>}
                    </div>
                    <button onClick={() => removeStone(i)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', padding: 0, marginLeft: 2 }}>×</button>
                  </div>
                ))}
              </div>
            )}
          </Field>
        </div>

        <Field label="Name (Khmer)">
          <input value={form.name_kh} onChange={e => set('name_kh', e.target.value)} style={inputStyle} placeholder="ខ្មែរ" />
        </Field>
        <Field label="Name (English)">
          <input value={form.name_en} onChange={e => set('name_en', e.target.value)} style={inputStyle} />
        </Field>
        <Field label="Name (Chinese)">
          <input value={form.name_cn} onChange={e => set('name_cn', e.target.value)} style={inputStyle} placeholder="中文" />
        </Field>
        <Field label="Selling Price (USD) *">
          <input type="number" step="0.01" value={form.selling_price} onChange={e => set('selling_price', e.target.value)} style={inputStyle} placeholder="0.00" />
        </Field>
        <Field label="Cost">
          <div style={{ display: 'flex', gap: 8 }}>
            <select value={form.source} onChange={e => set('source', e.target.value)} style={{ ...inputStyle, width: 90 }}>
              <option value="C">C (RMB)</option>
              <option value="M">M (MYR)</option>
              <option value="U">U (USD)</option>
            </select>
            <input type="number" step="0.01" value={form.cost_price} onChange={e => set('cost_price', e.target.value)} style={inputStyle} placeholder="0.00" />
          </div>
        </Field>
        <Field label="Quantity">
          <input type="number" value={form.quantity} onChange={e => set('quantity', e.target.value)} style={inputStyle} />
        </Field>
        <Field label="Low Stock Alert">
          <input type="number" value={form.low_stock_alert} onChange={e => set('low_stock_alert', e.target.value)} style={inputStyle} />
        </Field>
        <Field label="Photo URL" >
          <input value={form.photo_url} onChange={e => set('photo_url', e.target.value)} style={inputStyle} placeholder="https://..." />
        </Field>
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20, paddingTop: 16, borderTop: '1px solid #e5e7eb' }}>
        <button onClick={onClose} style={btnGhost}>Cancel</button>
        <button onClick={submit} disabled={saving} style={btnPrimary}>{saving ? 'Saving...' : 'Add Item'}</button>
      </div>
    </Modal>
  );
}

// ─── Edit Stock Modal ─────────────────────────────────────────────────────────
function EditStockModal({ item, onClose, onSaved }) {
  const [form, setForm] = useState({
    name_kh: item.name_kh || '',
    name_en: item.name_en || '',
    name_cn: item.name_cn || '',
    cost_price: item.cost_price || '',
    selling_price: item.selling_price || '',
    source: item.source || 'C',
    quantity: item.quantity ?? 0,
    low_stock_alert: item.low_stock_alert ?? 5,
    photo_url: item.photo_url || '',
    is_active: item.is_active ?? 1,
  });
  const [stones, setStones] = useState([]);

  // Load stone names on open
  useEffect(() => {
    const codes = item.stone_codes ? item.stone_codes.split('/') : (item.stone_code ? [item.stone_code] : []);
    if (codes.length === 0) return;
    Promise.all(codes.map(sc => {
      const match = sc.match(/^(\d+)(.+)$/);
      if (!match) return Promise.resolve({ code: sc, sub: '', name_en: sc, name_kh: '', name_cn: '' });
      const code = match[1].padStart(2, '0');
      const sub = match[2];
      return api.get('/stock/stones', { params: { code } })
        .then(r => r.data.find(s => s.code === code && s.sub === sub) || { code, sub, name_en: sc, name_kh: '', name_cn: '' })
        .catch(() => ({ code, sub, name_en: sc, name_kh: '', name_cn: '' }));
    })).then(results => setStones(results));
  }, []);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const addStone = stone => {
    const code = `${stone.code}${stone.sub}`;
    if (stones.find(s => `${s.code}${s.sub}` === code)) return;
    setStones(prev => [...prev, stone]);
  };
  const removeStone = idx => setStones(prev => prev.filter((_, i) => i !== idx));
  const stoneCodes = stones.map(s => `${s.code}${s.sub}`).join('/');

  const submit = async () => {
    setSaving(true);
    try {
      await api.put(`/stock/items/${item.id}`, {
        ...form,
        stone_code: stones[0] ? `${stones[0].code}${stones[0].sub}` : item.stone_code,
        stone_codes: stoneCodes || item.stone_codes,
      });
      toast.success('Updated');
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setSaving(false); }
  };

  return (
    <Modal title={`Edit — ${item.item_code}`} onClose={onClose} wide>
      {/* Item code — locked */}
      <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 6, padding: '8px 14px', fontSize: 14, fontFamily: 'monospace', color: '#374151', fontWeight: 700, marginBottom: 16 }}>
        {item.item_code} <span style={{ fontSize: 11, fontWeight: 400, color: '#9ca3af' }}>(code locked)</span>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        {/* Stone types — editable */}
        <div style={{ gridColumn: '1/-1' }}>
          <Field label="Stone Type(s)">
            <StoneSearch onSelect={addStone} placeholder="Add stone type..." />
            {stones.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
                {stones.map((s, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 4, background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, padding: '5px 10px', fontSize: 12 }}>
                    <span style={{ fontFamily: 'monospace', fontWeight: 700, color: '#1d4ed8' }}>{s.code}{s.sub}</span>
                    <div>
                      <div style={{ color: '#374151', fontWeight: 500 }}>{s.name_en || ''}</div>
                      {(s.name_kh || s.name_cn) && <div style={{ fontSize: 11, color: '#6b7280' }}>{s.name_kh}{s.name_kh && s.name_cn ? ' / ' : ''}{s.name_cn}</div>}
                    </div>
                    <button onClick={() => removeStone(i)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', padding: 0, marginLeft: 2 }}>×</button>
                  </div>
                ))}
              </div>
            )}
          </Field>
        </div>

        <Field label="Name (Khmer)">
          <input value={form.name_kh} onChange={e => set('name_kh', e.target.value)} style={inputStyle} />
        </Field>
        <Field label="Name (English)">
          <input value={form.name_en} onChange={e => set('name_en', e.target.value)} style={inputStyle} />
        </Field>
        <Field label="Name (Chinese)">
          <input value={form.name_cn} onChange={e => set('name_cn', e.target.value)} style={inputStyle} />
        </Field>
        <Field label="Selling Price (USD)">
          <input type="number" step="0.01" value={form.selling_price} onChange={e => set('selling_price', e.target.value)} style={inputStyle} />
        </Field>
        <Field label="Cost">
          <div style={{ display: 'flex', gap: 8 }}>
            <select value={form.source} onChange={e => set('source', e.target.value)} style={{ ...inputStyle, width: 90 }}>
              <option value="C">C (RMB)</option>
              <option value="M">M (MYR)</option>
              <option value="U">U (USD)</option>
            </select>
            <input type="number" step="0.01" value={form.cost_price} onChange={e => set('cost_price', e.target.value)} style={inputStyle} />
          </div>
        </Field>
        <Field label="Quantity">
          <input type="number" value={form.quantity} onChange={e => set('quantity', e.target.value)} style={inputStyle} />
        </Field>
        <Field label="Low Stock Alert">
          <input type="number" value={form.low_stock_alert} onChange={e => set('low_stock_alert', e.target.value)} style={inputStyle} />
        </Field>
        <Field label="Photo URL">
          <input value={form.photo_url} onChange={e => set('photo_url', e.target.value)} style={inputStyle} />
        </Field>
        <Field label="Status">
          <select value={form.is_active} onChange={e => set('is_active', parseInt(e.target.value))} style={inputStyle}>
            <option value={1}>Active</option>
            <option value={0}>Inactive / Out of Stock</option>
          </select>
        </Field>
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20, paddingTop: 16, borderTop: '1px solid #e5e7eb' }}>
        <button onClick={onClose} style={btnGhost}>Cancel</button>
        <button onClick={submit} disabled={saving} style={btnPrimary}>{saving ? 'Saving...' : 'Update'}</button>
      </div>
    </Modal>
  );
}

// ─── Manage Categories Modal ──────────────────────────────────────────────────
function ManageCategoriesModal({ onClose, onChanged }) {
  const [cats, setCats] = useState([]);
  const [form, setForm] = useState({ code: '', name_en: '', name_kh: '', name_cn: '' });
  const [editId, setEditId] = useState(null);

  const load = async () => { const r = await api.get('/stock/categories'); setCats(r.data); };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      if (editId) { await api.put(`/stock/categories/${editId}`, form); toast.success('Updated'); }
      else { await api.post('/stock/categories', form); toast.success('Added'); }
      setForm({ code: '', name_en: '', name_kh: '', name_cn: '' }); setEditId(null);
      load(); onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const del = async (id, code) => {
    if (!window.confirm(`Delete category ${code}?`)) return;
    try { await api.delete(`/stock/categories/${id}`); toast.success('Deleted'); load(); onChanged(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  return (
    <Modal title="Manage Categories" onClose={onClose} wide>
      <div style={{ display: 'grid', gridTemplateColumns: '70px 1fr 1fr 1fr auto', gap: 8, marginBottom: 14, alignItems: 'end' }}>
        {[['Code', 'code', 'BR', true], ['English', 'name_en', ''], ['Khmer', 'name_kh', 'ខ្មែរ'], ['Chinese', 'name_cn', '中文']].map(([label, key, ph, dis]) => (
          <div key={key}>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>{label}</div>
            <input value={form[key]} onChange={e => setForm(f => ({ ...f, [key]: key === 'code' ? e.target.value.toUpperCase() : e.target.value }))}
              style={inputStyle} placeholder={ph} disabled={dis && !!editId} />
          </div>
        ))}
        <div style={{ display: 'flex', gap: 6 }}>
          <button onClick={save} style={btnPrimary}>{editId ? 'Update' : 'Add'}</button>
          {editId && <button onClick={() => { setEditId(null); setForm({ code: '', name_en: '', name_kh: '', name_cn: '' }); }} style={btnGhost}>✕</button>}
        </div>
      </div>
      <div style={{ border: '1px solid #e5e7eb', borderRadius: 6, overflow: 'hidden', maxHeight: 400, overflowY: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead style={{ position: 'sticky', top: 0, background: '#f9fafb' }}>
            <tr>{['Code', 'English', 'Khmer', 'Chinese', ''].map(h => <th key={h} style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, color: '#374151', borderBottom: '1px solid #e5e7eb' }}>{h}</th>)}</tr>
          </thead>
          <tbody>
            {cats.map(c => (
              <tr key={c.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                <td style={{ padding: '8px 12px', fontFamily: 'monospace', fontWeight: 700, color: '#2563eb' }}>{c.code}</td>
                <td style={{ padding: '8px 12px', color: '#111827' }}>{c.name_en}</td>
                <td style={{ padding: '8px 12px', color: '#111827' }}>{c.name_kh}</td>
                <td style={{ padding: '8px 12px', color: '#111827' }}>{c.name_cn}</td>
                <td style={{ padding: '8px 12px', display: 'flex', gap: 6 }}>
                  <button onClick={() => { setEditId(c.id); setForm({ code: c.code, name_en: c.name_en || '', name_kh: c.name_kh || '', name_cn: c.name_cn || '' }); }} style={btnGhost}><Edit2 size={12} /></button>
                  <button onClick={() => del(c.id, c.code)} style={{ ...btnGhost, color: '#dc2626', borderColor: '#fca5a5' }}><X size={12} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Modal>
  );
}

// ─── Manage Stones Modal ──────────────────────────────────────────────────────
function ManageStonesModal({ onClose }) {
  const [stones, setStones] = useState([]);
  const [q, setQ] = useState('');
  const [form, setForm] = useState({ code: '', sub: '', name_en: '', name_kh: '', name_cn: '' });
  const [editId, setEditId] = useState(null);
  const timer = useRef(null);

  const load = async (search = '') => { const r = await api.get('/stock/stones', { params: search ? { q: search } : {} }); setStones(r.data); };
  useEffect(() => { load(); }, []);

  const handleQ = e => { setQ(e.target.value); clearTimeout(timer.current); timer.current = setTimeout(() => load(e.target.value), 300); };

  const save = async () => {
    try {
      if (editId) { await api.put(`/stock/stones/${editId}`, form); toast.success('Updated'); }
      else { await api.post('/stock/stones', { ...form, code: form.code.padStart(2, '0') }); toast.success('Added'); }
      setForm({ code: '', sub: '', name_en: '', name_kh: '', name_cn: '' }); setEditId(null); load(q);
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const del = async (id, code, sub) => {
    if (!window.confirm(`Delete stone ${code}${sub}?`)) return;
    try { await api.delete(`/stock/stones/${id}`); toast.success('Deleted'); load(q); }
    catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  return (
    <Modal title="Manage Stone Types" onClose={onClose} wide>
      <div style={{ display: 'grid', gridTemplateColumns: '60px 50px 1fr 1fr 1fr auto', gap: 8, marginBottom: 12, alignItems: 'end' }}>
        {[['Code', 'code', '01', true], ['Sub', 'sub', 'A', true], ['English', 'name_en', ''], ['Khmer', 'name_kh', 'ខ្មែរ'], ['Chinese', 'name_cn', '中文']].map(([label, key, ph, dis]) => (
          <div key={key}>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>{label}</div>
            <input value={form[key]} onChange={e => setForm(f => ({ ...f, [key]: key === 'sub' ? e.target.value.toUpperCase() : e.target.value }))}
              style={inputStyle} placeholder={ph} disabled={dis && !!editId} />
          </div>
        ))}
        <div style={{ display: 'flex', gap: 6 }}>
          <button onClick={save} style={btnPrimary}>{editId ? 'Update' : 'Add'}</button>
          {editId && <button onClick={() => { setEditId(null); setForm({ code: '', sub: '', name_en: '', name_kh: '', name_cn: '' }); }} style={btnGhost}>✕</button>}
        </div>
      </div>
      <div style={{ position: 'relative', marginBottom: 10 }}>
        <Search size={13} style={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
        <input value={q} onChange={handleQ} placeholder="Search stones..." style={{ ...inputStyle, paddingLeft: 28 }} />
      </div>
      <div style={{ border: '1px solid #e5e7eb', borderRadius: 6, overflow: 'hidden', maxHeight: 360, overflowY: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead style={{ position: 'sticky', top: 0, background: '#f9fafb', zIndex: 1 }}>
            <tr>{['Code', 'English', 'Khmer', 'Chinese', ''].map(h => <th key={h} style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, color: '#374151', borderBottom: '1px solid #e5e7eb' }}>{h}</th>)}</tr>
          </thead>
          <tbody>
            {stones.map(s => (
              <tr key={s.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                <td style={{ padding: '7px 12px', fontFamily: 'monospace', fontWeight: 700, color: '#2563eb' }}>{s.code}{s.sub}</td>
                <td style={{ padding: '7px 12px', color: '#111827' }}>{s.name_en}</td>
                <td style={{ padding: '7px 12px', color: '#111827', fontSize: 12 }}>{s.name_kh}</td>
                <td style={{ padding: '7px 12px', color: '#111827', fontSize: 12 }}>{s.name_cn}</td>
                <td style={{ padding: '7px 12px', display: 'flex', gap: 6 }}>
                  <button onClick={() => { setEditId(s.id); setForm({ code: s.code, sub: s.sub, name_en: s.name_en || '', name_kh: s.name_kh || '', name_cn: s.name_cn || '' }); }} style={btnGhost}><Edit2 size={12} /></button>
                  <button onClick={() => del(s.id, s.code, s.sub)} style={{ ...btnGhost, color: '#dc2626', borderColor: '#fca5a5' }}><X size={12} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Modal>
  );
}

// ─── Stone display in list ────────────────────────────────────────────────────
function StoneDisplay({ item }) {
  const codes = item.stone_codes ? item.stone_codes.split('/') : (item.stone_code ? [item.stone_code] : []);
  if (codes.length === 0) return <span style={{ color: '#9ca3af' }}>—</span>;
  if (codes.length === 1) {
    return (
      <div>
        <div style={{ fontSize: 12, color: '#374151', fontWeight: 500 }}><span style={{ fontFamily: 'monospace', fontWeight: 700 }}>{codes[0]}</span>{item.stone_name_kh ? ` - ${item.stone_name_kh}` : ''}</div>
        <div style={{ fontSize: 11, color: '#6b7280' }}>{item.stone_name_en || ''}{item.stone_name_cn ? ` / ${item.stone_name_cn}` : ''}</div>
      </div>
    );
  }
  return (
    <div>
      {codes.map((code, i) => (
        <span key={i} style={{ display: 'inline-block', fontFamily: 'monospace', fontSize: 11, background: '#f3f4f6', color: '#374151', padding: '1px 5px', borderRadius: 3, marginRight: 3, marginBottom: 2 }}>{code}</span>
      ))}
      <div style={{ fontSize: 11, color: '#374151', marginTop: 2 }}>{codes.join(' / ')} - {item.stone_name_kh || ''}</div>
      <div style={{ fontSize: 11, color: '#6b7280' }}>{item.stone_name_en || ''}{item.stone_name_cn ? ` / ${item.stone_name_cn}` : ''}</div>
    </div>
  );
}

// ─── Main Stock Page ──────────────────────────────────────────────────────────
export default function Stock() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [filterCat, setFilterCat] = useState('');
  const [filterStone, setFilterStone] = useState('');
  const [filterMin, setFilterMin] = useState('');
  const [filterMax, setFilterMax] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterQ, setFilterQ] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [showCats, setShowCats] = useState(false);
  const [showStones, setShowStones] = useState(false);
  const [userRole, setUserRole] = useState('');
  const stoneTimer = useRef(null);

  useEffect(() => {
    const u = JSON.parse(localStorage.getItem('user') || '{}');
    setUserRole(u.role || '');
    loadCategories();
  }, []);

  useEffect(() => { loadItems(); }, [page, filterCat, filterStatus, filterMin, filterMax]);

  const loadCategories = async () => { const r = await api.get('/stock/categories'); setCategories(r.data); };

  const loadItems = async () => {
    setLoading(true);
    try {
      const params = { page, per_page: 200 };
      if (filterCat) {
        const selectedCat = categories.find ? categories.find(c => c.id === parseInt(filterCat)) : null;
        if (selectedCat && PREFIX_CATS.includes(selectedCat.code)) params.prefix = selectedCat.code;
        else params.category_id = filterCat;
      }
      if (filterStone) params.stone_q = filterStone;
      if (filterMin) params.min_price = filterMin;
      if (filterMax) params.max_price = filterMax;
      if (filterStatus !== '') params.status = filterStatus;
      if (filterQ) params.q = filterQ;
      const r = await api.get('/stock/items', { params });
      setItems(r.data.items);
      setTotal(r.data.total);
      setPages(r.data.pages);
    } catch { toast.error('Failed to load stock'); }
    finally { setLoading(false); }
  };

  const handleSearch = () => { setPage(1); loadItems(); };
  const handleStoneFilter = e => {
    setFilterStone(e.target.value);
    clearTimeout(stoneTimer.current);
    stoneTimer.current = setTimeout(() => { setPage(1); loadItems(); }, 400);
  };

  const showCost = ['owner', 'admin'].includes(userRole);

  return (
    <div style={{ padding: 24, minHeight: '100vh', background: '#f8fafc' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#111827' }}>Stock</h1>
          <p style={{ margin: '2px 0 0', fontSize: 13, color: '#6b7280' }}>{total.toLocaleString()} items</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => setShowStones(true)} style={{ ...btnGhost, display: 'flex', alignItems: 'center', gap: 6 }}><Gem size={14} /> Stones</button>
          <button onClick={() => setShowCats(true)} style={{ ...btnGhost, display: 'flex', alignItems: 'center', gap: 6 }}><Tag size={14} /> Categories</button>
          <button onClick={() => setShowAdd(true)} style={{ ...btnPrimary, display: 'flex', alignItems: 'center', gap: 6 }}><Plus size={14} /> Add Stock</button>
        </div>
      </div>

      {/* Filters */}
      <div style={{ background: '#fff', borderRadius: 8, border: '1px solid #e5e7eb', padding: '14px 16px', marginBottom: 16, display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'flex-end' }}>
        <div style={{ flex: '1 1 160px' }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', marginBottom: 4 }}>SEARCH</div>
          <div style={{ position: 'relative' }}>
            <Search size={13} style={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
            <input value={filterQ} onChange={e => setFilterQ(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleSearch()} placeholder="Code, name..." style={{ ...inputStyle, paddingLeft: 28 }} />
          </div>
        </div>
        <div style={{ flex: '1 1 160px' }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', marginBottom: 4 }}>CATEGORY</div>
          <select value={filterCat} onChange={e => { setFilterCat(e.target.value); setPage(1); }} style={inputStyle}>
            <option value="">All</option>
            {categories.map(c => <option key={c.id} value={c.id}>{c.code} — {c.name_en}{c.name_kh ? ` / ${c.name_kh}` : ''}{c.name_cn ? ` / ${c.name_cn}` : ''}</option>)}
          </select>
        </div>
        <div style={{ flex: '1 1 140px' }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', marginBottom: 4 }}>STONE</div>
          <input value={filterStone} onChange={handleStoneFilter} placeholder="Name or code..." style={inputStyle} />
        </div>
        <div style={{ flex: '0 0 90px' }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', marginBottom: 4 }}>MIN $</div>
          <input type="number" value={filterMin} onChange={e => setFilterMin(e.target.value)} style={inputStyle} placeholder="0" />
        </div>
        <div style={{ flex: '0 0 90px' }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', marginBottom: 4 }}>MAX $</div>
          <input type="number" value={filterMax} onChange={e => setFilterMax(e.target.value)} style={inputStyle} placeholder="any" />
        </div>
        <div style={{ flex: '0 0 110px' }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', marginBottom: 4 }}>STATUS</div>
          <select value={filterStatus} onChange={e => { setFilterStatus(e.target.value); setPage(1); }} style={inputStyle}>
            <option value="">All</option>
            <option value="1">Active</option>
            <option value="0">Inactive</option>
          </select>
        </div>
        <button onClick={handleSearch} style={btnPrimary}>Search</button>
      </div>

      {/* Table */}
      <div style={{ background: '#fff', borderRadius: 8, border: '1px solid #e5e7eb', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
              {['Photo', 'Item Code', 'Category', 'Crystal / Stone', 'Name', ...(showCost ? ['Cost'] : []), 'Price', 'Qty', 'Status', ''].map(h => (
                <th key={h} style={{ padding: '10px 12px', textAlign: h === 'Cost' || h === 'Price' ? 'right' : h === 'Qty' || h === 'Status' ? 'center' : 'left', fontWeight: 600, color: '#374151' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={10} style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>Loading...</td></tr>}
            {!loading && items.length === 0 && (
              <tr><td colSpan={10} style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>
                <Package size={32} style={{ display: 'block', margin: '0 auto 8px', opacity: 0.3 }} />
                No stock items
              </td></tr>
            )}
            {items.map(item => (
              <tr key={item.id} style={{ borderBottom: '1px solid #f3f4f6' }}
                onMouseEnter={e => e.currentTarget.style.background = '#fafafa'}
                onMouseLeave={e => e.currentTarget.style.background = ''}>
                <td style={{ padding: '8px 12px' }}><PhotoCell url={item.photo_url} /></td>
                <td style={{ padding: '8px 12px' }}>
                  <span style={{ fontFamily: 'monospace', fontSize: 11, fontWeight: 700, color: '#1d4ed8', background: '#eff6ff', padding: '2px 7px', borderRadius: 4, whiteSpace: 'nowrap' }}>{item.item_code}</span>
                </td>
                <td style={{ padding: '8px 12px' }}>
                  {item.prefix && <span style={{ fontSize: 10, fontWeight: 700, background: '#fef3c7', color: '#92400e', padding: '1px 5px', borderRadius: 3, marginRight: 4 }}>{item.prefix}</span>}
                  <div style={{ fontSize: 12, color: '#374151', fontWeight: 500 }}><span style={{ fontFamily: 'monospace', fontWeight: 700 }}>{item.category_code}</span>{item.cat_name_kh ? ` - ${item.cat_name_kh}` : ''}</div>
                  <div style={{ fontSize: 11, color: '#6b7280' }}>{item.cat_name_en}</div>
                </td>
                <td style={{ padding: '8px 12px' }}><StoneDisplay item={item} /></td>
                <td style={{ padding: '8px 12px', maxWidth: 180 }}>
                  <div style={{ fontSize: 13, color: '#111827', fontWeight: 500 }}>{item.name_kh || item.name_en || '—'}</div>
                  {item.name_cn && <div style={{ fontSize: 11, color: '#9ca3af' }}>{item.name_cn}</div>}
                </td>
                {showCost && (
                  <td style={{ padding: '8px 12px', textAlign: 'right', fontFamily: 'monospace', fontSize: 12, color: '#6b7280', whiteSpace: 'nowrap' }}>
                    {item.source && item.cost_price ? `${SOURCE_LABELS[item.source] || item.source} ${item.cost_price}` : '—'}
                  </td>
                )}
                <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 600, color: '#111827', whiteSpace: 'nowrap' }}>
                  {item.selling_price ? `$${parseFloat(item.selling_price).toFixed(2)}` : '—'}
                </td>
                <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                  <span style={{ fontWeight: 600, color: item.quantity === 0 ? '#dc2626' : item.quantity <= item.low_stock_alert ? '#d97706' : '#16a34a' }}>{item.quantity}</span>
                </td>
                <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                  <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 10, background: item.is_active ? '#dcfce7' : '#f3f4f6', color: item.is_active ? '#16a34a' : '#9ca3af' }}>
                    {item.is_active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td style={{ padding: '8px 12px' }}>
                  <button onClick={() => setEditItem(item)} style={btnGhost} title="Edit"><Edit2 size={13} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {pages > 1 && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, padding: 14, borderTop: '1px solid #e5e7eb' }}>
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} style={btnGhost}><ChevronLeft size={14} /></button>
            <span style={{ fontSize: 13, color: '#6b7280' }}>Page {page} of {pages}</span>
            <button onClick={() => setPage(p => Math.min(pages, p + 1))} disabled={page === pages} style={btnGhost}><ChevronRight size={14} /></button>
          </div>
        )}
      </div>

      {showAdd && <AddStockModal categories={categories} onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); loadItems(); }} />}
      {editItem && <EditStockModal item={editItem} onClose={() => setEditItem(null)} onSaved={() => { setEditItem(null); loadItems(); }} />}
      {showCats && <ManageCategoriesModal onClose={() => setShowCats(false)} onChanged={loadCategories} />}
      {showStones && <ManageStonesModal onClose={() => setShowStones(false)} />}
    </div>
  );
}
